import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import LocalizedNavLabel from './LocalizedNavLabel';

const navItems = [
  { label: '首頁', path: '/' },
  { label: '品牌介紹', path: '/about' },
  { label: '合作通路', path: '/stores' },
  { label: '常見問答', path: '/faq' },
  { label: '閱讀推薦', path: '/blog' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '/home';

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isShrunk = scrolled || !isHome;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isShrunk
          ? 'bg-foreground-950/15 backdrop-blur-xl border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div
          className={`flex items-center justify-between transition-all duration-300 ${
            isShrunk ? 'h-12 md:h-14' : 'h-16 md:h-18'
          }`}
        >
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/d7679676-dd63-4051-b8b6-c3731369aef5/8c0d2f40-97f8-45c6-9a71-629ffe164689_compressed_logo-.webp"
              alt="山頂視角 PeakView"
              className={`transition-all duration-300 ${
                isShrunk ? 'h-6 md:h-7' : 'h-8 md:h-9'
              } w-auto`}
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-3 py-1.5 font-label font-medium whitespace-nowrap rounded-lg transition-all duration-300 ${
                    isShrunk ? 'text-xs' : 'text-sm'
                  } ${
                    isActive
                      ? isShrunk
                        ? 'text-white bg-white/15'
                        : 'text-white bg-white/15'
                      : isShrunk
                        ? 'text-white/80 hover:text-white hover:bg-white/10'
                        : 'text-white/85 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <LocalizedNavLabel href={item.path} fallback={item.label} />
                </Link>
              );
            })}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`flex items-center gap-1 font-label font-medium whitespace-nowrap rounded-lg transition-all duration-300 ${
                isShrunk ? 'text-xs px-3 py-1.5' : 'text-sm px-3 py-1.5'
              } ${
                isShrunk
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-white/75 hover:text-white hover:bg-white/10'
              }`}
            >
              書籍資料庫
              <i className="ri-external-link-line text-[10px]"></i>
            </a>
            <Link
              to="/about"
              className={`font-label font-medium whitespace-nowrap rounded-full transition-all duration-300 ${
                isShrunk
                  ? 'px-4 py-2 text-xs bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'px-5 py-2.5 text-sm bg-white/90 text-primary-700 hover:bg-white'
              }`}
            >
              了解品牌
            </Link>
          </div>

          <LanguageSwitcher />

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              isShrunk
                ? 'text-white hover:bg-white/10'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-foreground-950/90 backdrop-blur-xl border-b border-white/10">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 text-sm font-label font-medium rounded-lg transition-colors whitespace-nowrap ${
                    isActive
                      ? 'text-white bg-white/15'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <LocalizedNavLabel href={item.path} fallback={item.label} />
                </Link>
              );
            })}
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-3 text-sm font-label font-medium text-white/75 hover:text-white hover:bg-white/10 rounded-lg transition-colors whitespace-nowrap"
            >
              書籍資料庫
              <i className="ri-external-link-line text-xs"></i>
            </a>
            <Link
              to="/about"
              className="mt-2 px-4 py-3 text-sm font-label font-medium text-center bg-accent-500 text-primary-950 rounded-full hover:bg-accent-400 transition-colors whitespace-nowrap"
            >
              了解品牌
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
